import { Analytics } from '@cobliteam/analytics';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import { Check } from 'lucide-react';
import { useState } from 'react';

interface SpeedStepBodyProps {
  fleetId?: string;
  /** Called after the limit is applied, to advance to the next step. */
  onCompleted?: () => void;
}

const DEFAULT_KMH = 80;
/** Linger after success so the user sees the confirmation before advancing. */
const ADVANCE_DELAY_MS = 900;

/**
 * Inline "set allowed speed" — applies one allowed speed (km/h) to every vehicle in the fleet via the
 * `updateVehiclesSpeed` batch mutation (parallel saves behind a single settled handler: no per-vehicle
 * toast). This is purely the vehicle's allowed speed; no over-speeding alarm is involved. We fire it and
 * advance immediately (no awaiting): the maturity refetch reconciles the counts. Per-road (OSM) tuning
 * stays on the dedicated speed page.
 */
const SpeedStepBody = ({ fleetId, onCompleted }: SpeedStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const [kmh, setKmh] = useState(String(DEFAULT_KMH));
  const [done, setDone] = useState(false);
  const { data: vehicles } = useVehicle().getVehicles({});
  const { mutate: updateVehiclesSpeed } = useFuscaVehicle().updateVehiclesSpeed;

  const handleApply = () => {
    const limit = Number(kmh);
    const fleet = vehicles ?? [];
    if (!Number.isFinite(limit) || limit <= 0 || fleet.length === 0) return;
    // Fire-and-forget so the user isn't blocked while every vehicle saves in the background.
    updateVehiclesSpeed({ vehicles: fleet, speedLimitInKmh: limit });
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'speed' });
    setDone(true);
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  if (done) {
    return (
      <Typography variant='text-sm' className='flex items-center gap-1 text-success'>
        <Check size={16} aria-hidden />
        {t('inline.speed_applied')}
      </Typography>
    );
  }

  return (
    <div className='flex items-end gap-2'>
      <Input className='w-28' type='number' value={kmh} onChange={(event) => setKmh(event.target.value)} placeholder={t('inline.speed_kmh')} />
      <Button disabled={!kmh.trim()} onClick={handleApply}>
        {t('inline.speed_apply')}
      </Button>
    </div>
  );
};

export default SpeedStepBody;
